import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Sparkles, Trophy } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const HALL_PLANS = ['immortal', 'radiant'];

const LockedHall = () => (
  <Layout>
    <section className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-10">
      <div className="neon-card rounded-lg p-7 max-w-lg w-full text-center border-yellow-500/30 bg-yellow-950/5">
        <div className="mx-auto mb-4 w-12 h-12 rounded grid place-items-center border border-yellow-400/50 bg-yellow-500/10">
          <Crown className="w-6 h-6 text-yellow-300" />
        </div>
        <div className="text-xs uppercase tracking-widest text-yellow-300 mb-2">// premium leaderboard</div>
        <div className="font-mono-display text-3xl text-white mb-3">Hall of Fame</div>
        <p className="text-sm text-yellow-100/80 mb-2">Hall of Fame is only available for Immortal and Radiant subscribers.</p>
        <p className="text-sm text-zinc-400 mb-6">See the highest VP spent Valorant accounts on ValoRex.</p>
        <Link to="/pricing" className="btn-neon inline-flex justify-center">Upgrade Now</Link>
      </div>
    </section>
  </Layout>
);

const rankAccent = (position) => {
  if (position === 1) return 'border-yellow-400/60 bg-yellow-500/10 text-yellow-300';
  if (position === 2) return 'border-cyan-300/50 bg-cyan-400/10 text-cyan-200';
  if (position === 3) return 'border-orange-400/50 bg-orange-500/10 text-orange-300';
  return 'border-zinc-700 bg-black/30 text-zinc-300';
};

export default function HallOfFame() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const allowed = HALL_PLANS.includes((user?.plan || 'free').toLowerCase());

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/leaderboard/hall-of-fame', { params: { limit: 10 } });
        if (!cancelled) {
          setEntries(data.entries || []);
          setGeneratedAt(data.generatedAt || null);
        }
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 403) setError('locked');
          else setError(e?.response?.data?.detail || 'Failed to load Hall of Fame');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [allowed]);

  if (!allowed || error === 'locked') return <LockedHall />;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-yellow-300 mb-1">// leaderboard</div>
            <h1 className="font-mono-display text-4xl text-white flex items-center gap-3">
              <Trophy className="w-8 h-8 text-yellow-300" />Hall of Fame
            </h1>
            <p className="text-zinc-400 mt-2">Top ValoRex accounts ranked by total VP spent.</p>
          </div>
          <div className="neon-card rounded px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">Updated as of</div>
            <div className="font-mono-display text-sm text-cyan-200 tabular">
              {generatedAt ? new Date(generatedAt).toLocaleString() : 'Loading...'}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>
        )}

        {loading ? (
          <div className="neon-card rounded-lg p-10 text-center font-mono-display text-zinc-400">Loading Hall of Fame...</div>
        ) : entries.length === 0 ? (
          <div className="neon-card rounded-lg p-10 text-center">
            <Sparkles className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
            <div className="font-mono-display text-xl text-white mb-2">No accounts yet</div>
            <p className="text-sm text-zinc-400">The Hall of Fame will appear once ValoRex accounts are added.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {entries.map((entry) => (
              <div key={`${entry.position}-${entry.ign}-${entry.tag}`} className={`neon-card hall-of-fame-card rounded-lg p-5 ${entry.position <= 3 ? 'border-yellow-500/20' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className={`w-12 h-12 shrink-0 rounded grid place-items-center border font-mono-display text-lg ${rankAccent(entry.position)}`}>
                      #{entry.position}
                    </div>
                    <div className="min-w-0">
                      <div className="font-mono-display text-xl text-white truncate">
                        {entry.ign} <span className="text-orange-300">#{entry.tag}</span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">{entry.region} // Level {entry.level}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        Added {entry.addedAt ? new Date(entry.addedAt).toLocaleDateString() : 'Unknown'}
                      </div>
                    </div>
                  </div>
                  {entry.position <= 3 && <Crown className="w-5 h-5 text-yellow-300 shrink-0" />}
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="rounded border border-zinc-800 bg-black/30 py-2">
                    <div className="text-[10px] uppercase text-zinc-500">Total VP</div>
                    <div className="font-mono-display text-orange-300 tabular">{(entry.totalValueVP || 0).toLocaleString()}</div>
                  </div>
                  <div className="rounded border border-zinc-800 bg-black/30 py-2">
                    <div className="text-[10px] uppercase text-zinc-500">Skins</div>
                    <div className="font-mono-display text-cyan-300 tabular">{entry.skinsCount || 0}</div>
                  </div>
                  <div className="rounded border border-zinc-800 bg-black/30 py-2">
                    <div className="text-[10px] uppercase text-zinc-500">Rank</div>
                    <div className="font-mono-display text-white truncate px-1">{entry.rank?.name || 'Unrated'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
